// src/components/international/InternationalOrdersPanel.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Button,
  Alert,
  Chip,
  TablePagination,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import PublicIcon from "@mui/icons-material/Public";
import apiClient from "../../services/EnhancedApiClient";
import { useEnvironment } from "../../context/EnvironmentContext";

const InternationalOrdersPanel = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [scannedCount, setScannedCount] = useState(0);

  const { environment } = useEnvironment();

  const fetchOrders = useCallback(
    async (force = false) => {
      try {
        if (force) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        const response = await apiClient.fetchInternationalOrders({ force });

        if (response.orders && Array.isArray(response.orders)) {
          const sortedOrders = [...response.orders].sort((a, b) => {
            const aTime = a.timestamp ? new Date(a.timestamp).getTime() : NaN;
            const bTime = b.timestamp ? new Date(b.timestamp).getTime() : NaN;
            const aValid = !isNaN(aTime);
            const bValid = !isNaN(bTime);
            if (!aValid && !bValid) return 0;
            if (!aValid) return 1;
            if (!bValid) return -1;
            return bTime - aTime;
          });
          setOrders(sortedOrders);
          setScannedCount(response.scannedCount || 0);
        } else {
          setOrders([]);
        }

        setLastUpdated(new Date());
        setError(null);
      } catch (err) {
        console.error("Error fetching international orders:", err);
        setError("Failed to load international orders");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [environment]
  );

  useEffect(() => {
    fetchOrders(true);
  }, [environment, fetchOrders]);

  const handleRefresh = () => {
    fetchOrders(true);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getPagedData = () => {
    return orders.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  };

  const formatAmount = (amount) => {
    if (!amount || amount <= 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          N/A
        </Typography>
      );
    }
    const num = typeof amount === "string" ? parseFloat(amount.replace(/[$,]/g, "")) : amount;
    return `$${num.toFixed(2)}`;
  };

  return (
    <Paper id="panel-international-orders" sx={{ p: 3, mb: 3 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box display="flex" alignItems="center">
          <PublicIcon sx={{ mr: 1, color: "info.main" }} />
          <Typography variant="h5" component="h2">
            International Orders
            {environment === "staging" && (
              <Chip label="STAGING" size="small" color="warning" sx={{ ml: 1 }} />
            )}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 2 }}>
              Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
            size="small"
          >
            {refreshing ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </Box>

      <Box mb={2}>
        <Typography variant="body2" color="text.secondary">
          Completed orders where either billing or shipping address is outside
          the United States. These orders require manual review.
        </Typography>
      </Box>

      {loading && !refreshing ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ my: 2 }}>
          {error}
        </Alert>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              {orders.length} international order{orders.length !== 1 ? "s" : ""} found
              {scannedCount > 0 && ` (${scannedCount} total orders scanned)`}
              {environment === "staging" ? " in staging" : ""}
            </Typography>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Order #</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Billing Country</TableCell>
                  <TableCell>Shipping Country</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.length > 0 ? (
                  getPagedData().map((order) => (
                    <TableRow
                      key={order.orderNumber}
                      hover
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                    >
                      <TableCell component="th" scope="row">
                        <Typography variant="body2" fontWeight="medium">
                          {order.orderNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.customerName || "Unknown"}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
                          {order.email || "Unknown"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.billingCountry || "Unknown"}
                          size="small"
                          color={order.billingCountry && order.billingCountry !== "USA" && order.billingCountry !== "US" ? "info" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={order.shippingCountry || "Unknown"}
                          size="small"
                          color={order.shippingCountry && order.shippingCountry !== "USA" && order.shippingCountry !== "US" ? "info" : "default"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {formatAmount(order.amount || order.orderTotal)}
                      </TableCell>
                      <TableCell>
                        {order.timestamp
                          ? new Date(order.timestamp).toLocaleString()
                          : "Unknown"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No international orders found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {orders.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={orders.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count} international orders`
              }
            />
          )}
        </>
      )}
    </Paper>
  );
};

export default InternationalOrdersPanel;
